import React, { useState, useEffect, useRef } from 'react';
import { ImageOff, Building2 } from 'lucide-react';

interface OptimizedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  className?: string;
  fallbackSrc?: string;
  priority?: boolean;
  watermark?: boolean;
}

export const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  className = '',
  fallbackSrc = '/mount-cameroon-bg.webp',
  priority = false,
  watermark = true,
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [imgSrc, setImgSrc] = useState(src);
  const [isInView, setIsInView] = useState(priority);
  const containerRef = useRef<HTMLDivElement>(null);

  // IntersectionObserver for efficient viewport-based lazy loading
  useEffect(() => {
    if (priority || isInView) return;

    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '250px 0px' }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [priority, isInView]);

  // Maintain crisp resolution while respecting bandwidth and preferring modern WebP
  useEffect(() => {
    let effectiveSrc = src;
    if (effectiveSrc.startsWith('/') && !effectiveSrc.endsWith('.webp') && !effectiveSrc.endsWith('.svg') && !effectiveSrc.startsWith('data:')) {
      effectiveSrc = effectiveSrc.replace(/\.(jpg|jpeg|png)$/i, '.webp');
    }
    if (typeof navigator !== 'undefined' && 'connection' in navigator) {
      const conn = (navigator as any).connection;
      const isLowBandwidth =
        conn?.saveData === true ||
        conn?.effectiveType === 'slow-2g' ||
        conn?.effectiveType === '2g';

      // Keep resolution sharp (min 800px width, quality 75) so photos never appear blurry
      if (isLowBandwidth && effectiveSrc.includes('images.unsplash.com')) {
        effectiveSrc = effectiveSrc.replace(/w=\d+/, 'w=800').replace(/q=\d+/, 'q=75');
      }
    }
    setImgSrc(effectiveSrc);
    setIsLoaded(false);
    setHasError(false);
  }, [src]);

  const handleError = () => {
    if (imgSrc !== fallbackSrc) {
      setImgSrc(fallbackSrc);
    } else {
      setHasError(true);
    }
  };

  return (
    <div ref={containerRef} className={`relative overflow-hidden ${className}`}>
      {/* Shimmer skeleton while image loads or waits for viewport */}
      {(!isLoaded || !isInView) && !hasError && (
        <div className="absolute inset-0 bg-slate-200 animate-pulse flex items-center justify-center">
          <Building2 className="w-6 h-6 text-slate-300" />
        </div>
      )}

      {hasError ? (
        <div className="absolute inset-0 bg-slate-100 flex flex-col items-center justify-center text-slate-400 p-2 text-center">
          <ImageOff className="w-5 h-5 mb-1 text-slate-300" />
          <span className="text-[10px] font-medium">{alt || 'Image indisponible'}</span>
        </div>
      ) : (
        isInView && (
          <>
            <img
              src={imgSrc}
              alt={alt}
              width={props.width || 400}
              height={props.height || 260}
              loading={priority ? 'eager' : 'lazy'}
              decoding="async"
              referrerPolicy="no-referrer"
              onLoad={() => setIsLoaded(true)}
              onError={handleError}
              className={`w-full h-full object-cover transition-opacity duration-300 ${
                isLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              {...props}
            />

            {/* Small Bottom-Right Watermark Stamp (only if image is not already canvas-watermarked) */}
            {watermark && isLoaded && !imgSrc.includes('_watermarked') && (
              <div className="absolute bottom-1.5 right-1.5 z-10 bg-[#0e1f16]/80 backdrop-blur-xs border border-[#d97706]/50 px-1.5 py-0.5 rounded text-[8px] text-[#fffefb] font-semibold tracking-wide flex items-center gap-1 shadow-xs select-none pointer-events-none">
                <span className="text-[#facc15] text-[7px] leading-none">★</span>
                <span className="truncate max-w-[115px]">cameroonlandmarket.com</span>
              </div>
            )}
          </>
        )
      )}
    </div>
  );
};
